import * as THREE from 'three';
import type { PlayerStats } from '../player';
import type { Inventory, ItemId, ItemStack } from '../inventory';
import type { PlayerWallet } from '../wallet';
import type { ShipUpgrades, UpgradeId } from '../upgrades';
import type { FactionId, FactionSelection } from '../factions';
import type { MainScene, PlanetSurfaceScene } from '../../scenes';
import type { GameModeId } from '../flow/GameMode';
import type {
  MissionEventBus,
  MissionId,
  MissionManager,
  MissionSavePayload,
  MissionStepId
} from '../missions';

/**
 * Serialised game state. Versioned so future saves can extend the
 * schema without breaking existing files. Anything missing in an old
 * save is filled in with safe defaults at load time.
 */
export interface SaveDataV1 {
  readonly $schema: 'starfall-factions-save';
  readonly version: 1;
  /** Human-readable timestamp — surfaced in future "load slot" lists. */
  readonly savedAt: string;
  readonly faction: {
    readonly factionId: FactionId;
    readonly primaryColor: number;
  };
  readonly wallet: { readonly credits: number };
  readonly stats: {
    readonly hull: number;
    readonly fuel: number;
    readonly health: number;
    readonly oxygen: number;
  };
  readonly upgrades: ReadonlyArray<UpgradeId>;
  readonly shipInventory: ReadonlyArray<ItemStack | null>;
  readonly fpsInventory: ReadonlyArray<ItemStack | null>;
  readonly ship: {
    readonly position: readonly [number, number, number];
    readonly rotation: readonly [number, number, number];
  };
  readonly mode: GameModeId;
  /** When the player saved while on a planet (FPS or surface-ship),
   *  this snapshot describes where to place them on load. Null
   *  whenever the save was taken in space or station. */
  readonly surface: SurfaceSavePayload | null;
  /** Mission system state: active mission/step, current progress
   *  counter, completed mission ids. Null on saves predating the
   *  mission system — `apply()` falls back to a fresh start. */
  readonly missions: PersistedMissionPayload | null;
  readonly meta: Record<string, unknown> | null;
}

/** Persisted mission state. Mirrors the runtime `MissionSavePayload`
 *  but with mutable arrays so JSON survives the round-trip cleanly. */
export interface PersistedMissionPayload {
  readonly activeMissionId: MissionId | null;
  readonly activeStepId: MissionStepId | null;
  readonly currentCount: number;
  readonly completedMissionIds: ReadonlyArray<MissionId>;
}

/** Surface-mode payload — enough to drop the player back exactly
 *  where they paused on a planet. The astronaut yaw is preserved so
 *  the camera doesn't snap on load. */
export interface SurfaceSavePayload {
  /** Display name of the planet/moon (matches a Landable.displayName). */
  readonly planetName: string;
  readonly astronaut: {
    readonly position: readonly [number, number, number];
    readonly rotation: readonly [number, number, number];
  };
  readonly ship: {
    readonly position: readonly [number, number, number];
    readonly rotation: readonly [number, number, number];
  };
}

export type SaveData = SaveDataV1;

export interface SaveServiceDependencies {
  readonly stats: PlayerStats;
  readonly wallet: PlayerWallet;
  readonly shipInventory: Inventory;
  readonly fpsInventory: Inventory;
  readonly upgrades: ShipUpgrades;
  readonly faction: FactionSelection;
  readonly spaceScene: MainScene;
  /** Used to read the active surface scene when the save is taken on
   *  a planet — astronaut/ship transforms + planet name come from
   *  here. The surface scene is reused by every planet, so we only
   *  read it when the active mode is on-foot or in-ship-on-surface. */
  readonly surfaceScene: PlanetSurfaceScene;
  /** Returns the currently active game mode. */
  readonly getMode: () => GameModeId;
  /** Mission tracker — read for snapshot, write on apply. Optional so
   *  any boot path that hasn't constructed it yet still produces a
   *  valid save (the mission slot just round-trips as null). */
  readonly missionManager?: MissionManager;
  /** Mission event bus — used to broadcast `gameSaved` so the tutorial
   *  can detect that the player learned to save. Optional for the
   *  same reason as `missionManager`. */
  readonly missionBus?: MissionEventBus;
}

const SAVE_SCHEMA = 'starfall-factions-save';
const SAVE_VERSION = 1;

/**
 * Owns the lifecycle of a single play-session save: read state from all
 * gameplay subsystems → JSON, and apply parsed JSON back to those same
 * subsystems. Decoupled from any UI — the pause menu and load overlay
 * both call into this service.
 *
 * Save files are plain JSON wrapped in a `.txt` file for the download.
 * No binary formats / no localStorage — keeping it simple while we
 * iterate. Future versions can layer LocalStorage slots on top of this
 * same snapshot/parse pipeline.
 */
export class SaveGameService {
  constructor(private readonly deps: SaveServiceDependencies) {}

  /** Capture the current game state as a SaveData object. */
  snapshot(): SaveDataV1 {
    const ship = this.deps.spaceScene.getPlayerShip();
    const pos = ship?.object3D.position ?? new THREE.Vector3(0, 0, 0);
    const rot = ship?.object3D.rotation ?? new THREE.Euler(0, 0, 0);
    const mode = this.deps.getMode();

    return {
      $schema: SAVE_SCHEMA,
      version: SAVE_VERSION,
      savedAt: new Date().toISOString(),
      faction: {
        factionId: this.deps.faction.factionId,
        primaryColor: this.deps.faction.primaryColor
      },
      wallet: {
        credits: this.deps.wallet.balance
      },
      stats: {
        hull: this.deps.stats.hull,
        fuel: this.deps.stats.fuel,
        health: this.deps.stats.health,
        oxygen: this.deps.stats.oxygen
      },
      upgrades: Array.from(this.deps.upgrades.snapshot()),
      shipInventory: this.deps.shipInventory.snapshot().map((s) =>
        s ? { itemId: s.itemId, count: s.count } : null
      ),
      fpsInventory: this.deps.fpsInventory.snapshot().map((s) =>
        s ? { itemId: s.itemId, count: s.count } : null
      ),
      ship: {
        position: [pos.x, pos.y, pos.z],
        rotation: [rot.x, rot.y, rot.z]
      },
      mode,
      surface: this.snapshotSurface(mode),
      missions: this.snapshotMissions(),
      meta: null
    };
  }

  private snapshotMissions(): PersistedMissionPayload | null {
    const mgr = this.deps.missionManager;
    if (!mgr) return null;
    const snap: MissionSavePayload = mgr.saveSnapshot();
    return {
      activeMissionId: snap.activeMissionId,
      activeStepId: snap.activeStepId,
      currentCount: snap.currentCount,
      completedMissionIds: [...snap.completedMissionIds]
    };
  }

  /** Reads the live astronaut + surface-ship transforms when the
   *  player is on a planet at save time. Returns null whenever the
   *  surface scene isn't active — saves taken in space or in the
   *  station resume from their respective default flows. */
  private snapshotSurface(mode: GameModeId): SurfaceSavePayload | null {
    if (mode !== 'surface-astronaut' && mode !== 'surface-ship') return null;
    const planetName = this.deps.surfaceScene.getPlanetName();
    if (!planetName) return null;
    let astronautPos = new THREE.Vector3();
    let astronautRot = new THREE.Euler();
    let surfaceShipPos = new THREE.Vector3();
    let surfaceShipRot = new THREE.Euler();
    try {
      const a = this.deps.surfaceScene.astronautEntity;
      astronautPos = a.object3D.position.clone();
      astronautRot = a.object3D.rotation.clone();
    } catch {
      // Astronaut hasn't been spawned yet — give up on surface payload.
      return null;
    }
    try {
      const s = this.deps.surfaceScene.shipEntity;
      surfaceShipPos = s.object3D.position.clone();
      surfaceShipRot = s.object3D.rotation.clone();
    } catch {
      return null;
    }
    return {
      planetName,
      astronaut: {
        position: [astronautPos.x, astronautPos.y, astronautPos.z],
        rotation: [astronautRot.x, astronautRot.y, astronautRot.z]
      },
      ship: {
        position: [surfaceShipPos.x, surfaceShipPos.y, surfaceShipPos.z],
        rotation: [surfaceShipRot.x, surfaceShipRot.y, surfaceShipRot.z]
      }
    };
  }

  /** Serialise snapshot to a pretty-printed JSON string. */
  serialize(data: SaveDataV1): string {
    return JSON.stringify(data, null, 2);
  }

  /** Convenience instance method — delegates to the static parser. */
  parse(raw: string): SaveDataV1 {
    return SaveGameService.parse(raw);
  }

  /** Validate + parse a string into a SaveData object. Throws if the
   *  payload isn't a valid Starfall save file. Future versions can add
   *  migration here without changing call sites.
   *
   *  Static so the intro-flow file picker can validate before the
   *  full service exists (the runtime instance needs all gameplay
   *  systems wired up, but parsing is pure). */
  static parse(raw: string): SaveDataV1 {
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch (err) {
      throw new Error(`Save file is not valid JSON: ${(err as Error).message}`);
    }
    if (!parsed || typeof parsed !== 'object') {
      throw new Error('Save file is empty or malformed.');
    }
    const obj = parsed as Record<string, unknown>;
    if (obj['$schema'] !== SAVE_SCHEMA) {
      throw new Error('Save file is not a Starfall Factions save.');
    }
    if (obj['version'] !== SAVE_VERSION) {
      // For now the only known version is 1. When we ship v2, add a
      // migration path here.
      throw new Error(
        `Unsupported save version: ${String(obj['version'])}. Expected ${SAVE_VERSION}.`
      );
    }
    return obj as unknown as SaveDataV1;
  }

  /** Apply a parsed snapshot back to the live game systems. Called
   *  during start-up if the player chose "Load Game" in the intro
   *  flow, and could later be called mid-run for quick-load support.
   *
   *  Wallet / stats / inventories / upgrades / space-ship pose all
   *  restore synchronously here. If the save also captured a surface
   *  restoration (player was on a planet), the caller still needs to
   *  invoke `GameDirector.restoreToSurface(data.surface)` after the
   *  director is initialised — the surface scene needs the director's
   *  fade + controller wiring, which lives outside this service. */
  apply(data: SaveDataV1): void {
    this.deps.wallet.setBalance(data.wallet.credits);
    this.deps.stats.restore({
      hull: data.stats.hull,
      fuel: data.stats.fuel,
      health: data.stats.health,
      oxygen: data.stats.oxygen
    });
    this.deps.upgrades.restore(data.upgrades);
    this.deps.shipInventory.restoreSlots(data.shipInventory);
    this.deps.fpsInventory.restoreSlots(data.fpsInventory);

    const ship = this.deps.spaceScene.getPlayerShip();
    if (ship && data.ship) {
      ship.object3D.position.set(
        data.ship.position[0] ?? 0,
        data.ship.position[1] ?? 0,
        data.ship.position[2] ?? 0
      );
      ship.object3D.rotation.set(
        data.ship.rotation[0] ?? 0,
        data.ship.rotation[1] ?? 0,
        data.ship.rotation[2] ?? 0
      );
      ship.velocity.set(0, 0, 0);
      // Tell the controller to reread the ship's transform so the
      // chase camera + steering targets match the loaded pose.
      this.deps.spaceScene.getShipControl()?.syncFromShip();
    }

    // Mission state — restore if the save carried it (older saves
    // round-trip null and the manager will start the tutorial fresh
    // when start() is later called by the boot path).
    if (this.deps.missionManager) {
      const payload = data.missions
        ? {
            activeMissionId: data.missions.activeMissionId ?? null,
            activeStepId: data.missions.activeStepId ?? null,
            currentCount: data.missions.currentCount ?? 0,
            completedMissionIds: data.missions.completedMissionIds ?? []
          }
        : null;
      this.deps.missionManager.restore(payload);
    }
  }

  /** Trigger a browser download of the current state as a `.txt`
   *  file. File name embeds the save timestamp + faction so the
   *  player can keep multiple snapshots. */
  download(): void {
    const data = this.snapshot();
    const json = this.serialize(data);
    const blob = new Blob([json], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const fileName = `starfall-${data.faction.factionId}-${stamp}.txt`;
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Defer revoke so the navigator finishes the download.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    // Tutorial listens for this so the "learn to save" step can flip
    // to complete the moment the player triggers a download.
    this.deps.missionBus?.emit('gameSaved', {});
  }
}
